/**
 * Central consent state for non-essential browser storage and third-party tags.
 * Necessary session/security storage is outside this service.
 */

export const CONSENT_STORAGE_KEY = "locreach_cookie_consent_v1";
export const CONSENT_VERSION = "2026-08-05";
export const CONSENT_EXPIRY_DAYS = 180;
export const CONSENT_CHANGED_EVENT = "locreach:consent-changed";

export const DEFAULT_CONSENT = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
});

const isBrowser = () => typeof window !== "undefined";

export function isValidStoredConsent(value) {
  if (!value || typeof value !== "object") return false;
  if (value.version !== CONSENT_VERSION) return false;
  if (!value.expiresAt) return false;

  const expiresAt = new Date(value.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

export function readCookieConsent() {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return isValidStoredConsent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function expiryDate() {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + CONSENT_EXPIRY_DAYS);
  return expiresAt.toISOString();
}

export function buildCookieConsent({ analytics, marketing, source }) {
  return {
    necessary: true,
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
    version: CONSENT_VERSION,
    source: String(source || "preferences"),
    savedAt: new Date().toISOString(),
    expiresAt: expiryDate(),
  };
}

export function saveCookieConsent(consent) {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Consent UI must still work when storage is unavailable.
  }

  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, {
      detail: consent,
    })
  );
}

export function hasAnalyticsConsent() {
  return Boolean(readCookieConsent()?.analytics);
}

export function hasMarketingConsent() {
  return Boolean(readCookieConsent()?.marketing);
}

export function hasAnyMeasurementConsent() {
  const consent = readCookieConsent();
  return Boolean(consent?.analytics || consent?.marketing);
}
